import { Dialog } from '@components/dialog/Dialog';
import { Typography } from '@mui/material';
import { useQuote } from '@services/quotes';
import { GridColDef } from '@mui/x-data-grid';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { dataGrid } from '@components/dataGrid/DataGrid.styles';
import { getEventRows } from './getEventRows';
import { useSearchQuoteItemCoverages } from '@services/quoteItem';
import { getPremiumRows } from '@pages/BindQuote/SubmissionSummary/getPremiumRows';
import { Grid } from '@mui/material';
import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox/Checkbox';
import { FormControlLabel } from '@components/formControlLabel';
import { getQuoteTermRows } from '@pages/BindQuote/SubmissionSummary/quoteTermsConditionsRows';
import { useSearchQuoteDocumentFormSet } from '@services/document/useSearchQuoteDocumentFormSet';
import { DocumentFormSetPostResponse, DocumentFormSetSearchResponse } from '@lio/api-types';
import { PackageTypeId } from '@models/document';
import { useCreateQuoteDeclaration } from '@services/quotes/quoteDeclaration';
import { DeclarationType } from '@lio/rule-engine';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { DataGrid } from '@components/dataGrid/Datagrid';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { shallow } from 'zustand/shallow';

type DocumentFormSet = DocumentFormSetSearchResponse['items'][0]['documentForms'][0];
type SubmissionSummaryProps = {
  quoteId: string;
  open: boolean;
  handleClose: () => void;
  payAndBind?: boolean;
  acknowledgeForms?: boolean;
  setAcknowledgeForms?: (acknowledgeForms: boolean) => void;
  handleContinuePayment?: () => void;
  isPaymentDisabled?: boolean;
};

const columns: GridColDef[] = [
  {
    field: 'Title',
    headerName: '',
    flex: 2,
    sortable: false,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Grid
          sx={
            params.row.sectionHeader &&
            !params.row.title.includes('Event') && {
              paddingTop: 1,
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            }
          }
        >
          <Typography
            variant='bodySmall'
            sx={{
              paddingLeft: params.row.titleRow || params.row.sectionHeader ? 0 : { xs: 1, sm: 3 },
              color: 'text.subdued',
              display: 'block',
              my: 0,
            }}
            color={params.row.sectionHeader ? 'text.main' : 'text.subdued'}
            fontWeight={params.row.sectionHeader ? 'bold' : 'thin'}
          >
            {params.row.title ?? '-'}
          </Typography>
        </Grid>
      );
    },
  },
  {
    headerAlign: 'right',
    flex: 2,
    sortable: false,
    align: 'right',
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Grid
          sx={
            params.row.sectionHeader &&
            !params.row.title.includes('Event') && {
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            }
          }
        >
          <Typography
            variant='bodySmall'
            sx={{
              color: 'text.main',
              fontWeight: 'bold',
              marginRight: '16px',
            }}
          >
            {params.row.value}
          </Typography>
        </Grid>
      );
    },
    field: 'Detail',
    headerName: '',
  },
];

const declarationLabel = 'I acknowledge the forms listed above';

export const SubmissionSummary = ({
  quoteId,
  open,
  handleClose,
  setAcknowledgeForms,
  acknowledgeForms,
  handleContinuePayment,
  payAndBind = false,
  isPaymentDisabled = false,
}: SubmissionSummaryProps) => {
  const { quote } = useQuote(quoteId);
  const { data: quoteItemCoveragesResult, isLoading: isLoadingQuoteItemCoverages } = useSearchQuoteItemCoverages({
    quoteId,
  });

  const { isMobileScreen } = useGetScreenSize();
  const { createQuoteDeclaration, isPending } = useCreateQuoteDeclaration(quoteId);

  const { data: quoteDocumentFormSetResult } = useSearchQuoteDocumentFormSet(quoteId, {
    packageTypeId: PackageTypeId.NewBusiness,
    sort: '-createdAt',
    pageLimit: 1,
  });

  const formsIncludedInSchedule = (quoteDocumentFormSetResult?.items ?? [])
    .flatMap((item: DocumentFormSetPostResponse) =>
      item.documentForms.filter((df: DocumentFormSet) => !!df.form?.includeInSchedule || !!df.formVersion?.isScheduled),
    )
    .sort((a, b) => a.orderIndex - b.orderIndex);

  const { structuredQuote } = useQuoteStore((state) => ({ structuredQuote: state.structuredQuote }), shallow);
  if (!quote || !structuredQuote) {
    return null;
  }

  const quoteItemCoverages = isLoadingQuoteItemCoverages ? [] : (quoteItemCoveragesResult?.items ?? []);
  const formRows = formsIncludedInSchedule.map((form) => {
    const formNumber = form.form?.formNumber ?? form.formVersion?.form?.formNumber ?? '';
    const version = form.form?.version ?? form.formVersion?.edition ?? '';
    const name = form.form?.name ?? form.formVersion?.form?.name ?? '';
    return {
      id: `${formNumber}-${form.orderIndex}`,
      title: `${formNumber} ${version} ${name}`.trim(),
      value: '',
    };
  });

  const rows = [
    {
      sectionHeader: true,
      id: `Event-Details`,
      title: `Event Details`,
      value: '',
    },
    ...getEventRows(structuredQuote),
    { sectionHeader: true, id: `Premium-Details`, title: `Premium`, value: '' },
    ...getPremiumRows(quoteItemCoverages, structuredQuote),
    ...getQuoteTermRows(structuredQuote),
    { sectionHeader: true, id: `Forms`, title: `Forms`, value: '' },
  ];

  const handleSubmitDeclaration = async () => {
    await createQuoteDeclaration({
      declaration: { statements: [declarationLabel] },
      documentFormSetId: quoteDocumentFormSetResult?.items[0].id,
      declarationType: DeclarationType.FormsAndExclusions,
    });

    if (handleContinuePayment) {
      await handleContinuePayment();
    } else {
      handleClose();
    }
  };

  return (
    <Dialog
      size={isMobileScreen ? undefined : 'medium'}
      open={open}
      title={`Event Coverage Summary for #${quote.policyNumber?.policyNumber}`}
      handleCloseDialog={handleClose}
      sxProps={isMobileScreen ? {} : { paperSx: { maxHeight: '90vw' } }}
      footerContent={
        <Grid
          sx={{
            display: 'flex',
            justifyContent: payAndBind && !isPaymentDisabled ? 'space-between' : 'flex-end',
            p: { xs: 1, sm: 2 },
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          {setAcknowledgeForms && payAndBind && !isPaymentDisabled && (
            <FormControlLabel
              label={declarationLabel}
              value={true}
              checked={acknowledgeForms === true}
              control={
                <Checkbox
                  data-testid='Electronic Approval'
                  checked={acknowledgeForms === true}
                  onChange={() => setAcknowledgeForms && setAcknowledgeForms(!acknowledgeForms)}
                />
              }
            />
          )}
          <Grid sx={{ display: 'flex', gap: 2, mt: { xs: 1, sm: 0 } }}>
            <Button
              fullWidth={isMobileScreen}
              variant='outlined'
              color='primary'
              disabled={isPending}
              onClick={handleClose}
            >
              Exit
            </Button>
            {setAcknowledgeForms && payAndBind && !isPaymentDisabled && (
              <Button
                fullWidth={isMobileScreen}
                variant='contained'
                color='primary'
                onClick={handleSubmitDeclaration}
                disabled={(payAndBind && !acknowledgeForms) || isPending || isPaymentDisabled}
                isLoading={isPending}
              >
                Pay & Bind
              </Button>
            )}
          </Grid>
        </Grid>
      }
    >
      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          paddingRight: { xs: 0.5, sm: 2 },
          paddingLeft: { xs: 2, sm: 3 },
        }}
      >
        <DataGrid
          hideFooter
          initialState={{ density: 'compact' }}
          columns={columns}
          rows={rows}
          columnHeaderHeight={10}
          getRowHeight={() => 'auto'}
          sx={{
            ...gridStyles,
            ' .MuiDataGrid-cell': {
              px: 0,
            },
          }}
        />
        {formRows.length > 0 && (
          <DataGrid
            hideFooter
            initialState={{ density: 'compact' }}
            columns={[columns[0]]}
            rows={formRows}
            columnHeaderHeight={10}
            getRowHeight={() => 'auto'}
            sx={{
              ...gridStyles,
              ' .MuiDataGrid-cell': {
                px: 0,
              },
            }}
          />
        )}
      </Grid>
    </Dialog>
  );
};

const gridStyles = {
  ...dataGrid,
  height: 'fit-content',
  '& .MuiDataGrid-row': {
    border: 'none',
    paddingY: 0.5,
  },
  '& .MuiDataGrid-cell': {
    border: 'none',
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },
  '& .MuiDataGrid-columnHeader': {
    visibility: 'hidden',
  },
};
